import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link, useOutletContext } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  getProduct,
  updateProduct,
  deleteProduct,
  getProductImageUploadUrl,
  confirmProductImageUpload,
  deleteProductImage,
  setPrimaryProductImage,
  listProductPriceHistory,
} from '@vn-propane/shared/client';
import type {
  Product,
  ProductImage,
  ProductPriceHistoryEntry,
  UpdateProductRequest,
  ErrorResponse,
  UserOrganization,
} from '@vn-propane/shared/client';
import { ArrowLeft, ArrowRight, Loader2, Star, Trash2, Upload } from 'lucide-react';
import { formatPrice, getImageUrl, getPriceInputStep, getPricePlaceholder } from '@/lib/utils';

interface OrganizationContext {
  organization: UserOrganization;
}

export default function ProductDetail() {
  const { id: orgId, productId } = useParams<{ id: string; productId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation(['admin', 'common']);
  const { organization } = useOutletContext<OrganizationContext>();
  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');

  const [formData, setFormData] = useState<UpdateProductRequest>({
    name: undefined,
    sku: undefined,
    unit: undefined,
    price: undefined,
  });

  useEffect(() => {
    async function fetchProduct() {
      if (!orgId || !productId) return;

      try {
        const result = await getProduct({
          path: { org_id: orgId, id: productId },
        });

        if (result.error) {
          const errorResponse = result.error as ErrorResponse;
          if (result.response?.status === 401) {
            navigate('/login');
            return;
          }
          if (result.response?.status === 404) {
            navigate(`/organizations/${orgId}/products`);
            return;
          }
          setError(errorResponse.error?.message || t('products.failedToLoadProduct'));
          return;
        }

        if (result.data) {
          const p = result.data.data;
          setProduct(p);
          setFormData({
            name: p.name,
            sku: p.sku || undefined,
            unit: p.unit,
            price: p.price || undefined,
          });
        }
      } catch (err) {
        setError(t('products.failedToLoadProduct'));
        console.error(err);
      } finally {
        setLoading(false);
      }
    }

    fetchProduct();
  }, [orgId, productId, navigate]);

  const handleUpdate = async () => {
    if (!orgId || !productId) return;

    setSaving(true);
    const result = await updateProduct({
      path: { org_id: orgId, id: productId },
      body: formData,
    });

    if (result.error) {
      console.error('Failed to update product:', result.error);
      setSaving(false);
      return;
    }

    if (result.data) {
      setProduct(result.data.data);
    }
    setSaving(false);
  };

  const handleDelete = async () => {
    if (!orgId || !productId) return;

    const result = await deleteProduct({
      path: { org_id: orgId, id: productId },
    });

    if (result.error) {
      console.error('Failed to delete product:', result.error);
      return;
    }

    setIsDeleteOpen(false);
    setDeleteConfirmText('');
    navigate(`/organizations/${orgId}/products`);
  };

  if (loading) {
    return <div className="flex justify-center p-8">{t('common:labels.loading')}</div>;
  }

  if (error) {
    return <div className="text-red-500 p-4">{error}</div>;
  }

  if (!product) {
    return null;
  }

  return (
    <>
      <div className="max-w-2xl mx-auto">
        <div className="mb-6">
          <Link
            to={`/organizations/${orgId}/products`}
            className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="w-4 h-4" />
            {t('products.backToProducts')}
          </Link>
        </div>

        <div className="space-y-6">
          <div>
            <h1 className="text-2xl font-extrabold tracking-tight">{product.name}</h1>
            {product.sku && (
              <p className="text-muted-foreground">{t('products.sku')}: {product.sku}</p>
            )}
          </div>

          <div className="space-y-4">
            <h3 className="text-lg font-semibold">{t('products.productInformation')}</h3>

            <div className="space-y-2">
              <Label htmlFor="name">{t('products.productNameRequired')}</Label>
              <Input
                id="name"
                value={formData.name || ''}
                onChange={(e) => setFormData({ ...formData, name: e.target.value || undefined })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="sku">{t('products.sku')}</Label>
              <Input
                id="sku"
                value={formData.sku || ''}
                onChange={(e) => setFormData({ ...formData, sku: e.target.value || undefined })}
                placeholder="PROP-12KG"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="unit">{t('products.unitOfMeasureRequired')}</Label>
              <Input
                id="unit"
                value={formData.unit || ''}
                onChange={(e) => setFormData({ ...formData, unit: e.target.value || undefined })}
                placeholder={t('products.unitPlaceholder')}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="price">{t('products.price')} ({organization.currency})</Label>
              <Input
                id="price"
                type="number"
                step={getPriceInputStep(organization.currency)}
                min="0"
                value={formData.price || ''}
                onChange={(e) => setFormData({ ...formData, price: e.target.value || undefined })}
                placeholder={getPricePlaceholder(organization.currency)}
              />
            </div>

            <Button onClick={handleUpdate} disabled={!formData.name || !formData.unit || saving}>
              {saving ? t('common:buttons.saving') : t('products.saveChanges')}
            </Button>
          </div>

          <hr className="my-8" />

          <ProductImagesSection
            orgId={orgId!}
            productId={productId!}
            images={product.images ?? []}
            onChange={(images) => setProduct({ ...product, images })}
          />

          <hr className="my-8" />

          <PriceHistorySection
            orgId={orgId!}
            productId={productId!}
            currency={organization.currency}
            currentPrice={product.price}
          />

          <hr className="my-8" />

          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-destructive">{t('products.dangerZone')}</h3>
            <p className="text-sm text-muted-foreground">
              {t('products.deleteWarning')}
            </p>
            <Button variant="destructive" onClick={() => setIsDeleteOpen(true)}>
              {t('products.deleteProduct')}
            </Button>
          </div>
        </div>
      </div>

      <AlertDialog open={isDeleteOpen} onOpenChange={(open) => {
        setIsDeleteOpen(open);
        if (!open) setDeleteConfirmText('');
      }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('products.deleteConfirmTitle')}</AlertDialogTitle>
            <AlertDialogDescription className="space-y-4">
              <p>
                <Trans
                  i18nKey="admin:products.deleteConfirmMessage"
                  values={{ name: product.name }}
                  components={{ strong: <strong /> }}
                />
              </p>
              <p>
                <Trans
                  i18nKey="admin:products.deleteConfirmPrompt"
                  values={{ name: product.name }}
                  components={{ strong: <strong /> }}
                />
              </p>
              <Input
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                placeholder={t('products.typeToConfirm')}
              />
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common:buttons.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleteConfirmText !== product.name}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t('common:buttons.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

interface ProductImagesSectionProps {
  orgId: string;
  productId: string;
  images: ProductImage[];
  onChange: (images: ProductImage[]) => void;
}

function ProductImagesSection({ orgId, productId, images, onChange }: ProductImagesSectionProps) {
  const { t } = useTranslation(['admin']);
  const [uploading, setUploading] = useState(false);
  const [busyImageId, setBusyImageId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const sortedImages = [...images].sort((a, b) => a.displayOrder - b.displayOrder);

  const replaceImage = (next: ProductImage) => {
    onChange(images.map((img) => (img.id === next.id ? next : img)));
  };

  const handleUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setError(t('products.imageMustBeImage'));
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError(t('products.imageTooLarge'));
      return;
    }

    setUploading(true);
    setError(null);
    try {
      const uploadUrlResult = await getProductImageUploadUrl({
        path: { org_id: orgId, id: productId },
        body: {
          fileType: 'product_image',
          referenceId: productId,
          contentType: file.type,
          filename: file.name,
          fileSizeBytes: file.size,
        },
      });
      if (uploadUrlResult.error) {
        const err = uploadUrlResult.error as ErrorResponse;
        throw new Error(err.error?.message || t('products.imageUploadFailed'));
      }
      const { uploadId, uploadUrl } = uploadUrlResult.data!.data;

      const s3Response = await fetch(uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': file.type, 'x-amz-acl': 'public-read' },
        body: file,
      });
      if (!s3Response.ok) {
        throw new Error(t('products.imageUploadFailed'));
      }

      const confirmResult = await confirmProductImageUpload({
        path: { org_id: orgId, id: productId, upload_id: uploadId },
        body: { fileSizeBytes: file.size },
      });
      if (confirmResult.error) {
        const err = confirmResult.error as ErrorResponse;
        throw new Error(err.error?.message || t('products.imageUploadFailed'));
      }

      const newImage = confirmResult.data!.data.image;
      onChange([...images, newImage]);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('products.imageUploadFailed'));
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleSetPrimary = async (imageId: string) => {
    setBusyImageId(imageId);
    setError(null);
    try {
      const result = await setPrimaryProductImage({
        path: { org_id: orgId, id: productId, image_id: imageId },
      });
      if (result.error) {
        const err = result.error as ErrorResponse;
        throw new Error(err.error?.message || t('products.imageUploadFailed'));
      }
      onChange(
        images.map((img) => ({ ...img, isPrimary: img.id === imageId })),
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : t('products.imageUploadFailed'));
    } finally {
      setBusyImageId(null);
    }
  };

  const handleDelete = async (image: ProductImage) => {
    setBusyImageId(image.id);
    setError(null);
    try {
      const result = await deleteProductImage({
        path: { org_id: orgId, id: productId, image_id: image.id },
      });
      if (result.error) {
        const err = result.error as ErrorResponse;
        throw new Error(err.error?.message || t('products.imageDeleteFailed'));
      }
      const remaining = images.filter((img) => img.id !== image.id);
      // If we removed the primary, the backend auto-promotes the next image;
      // mirror that locally so the UI stays in sync without an extra fetch.
      if (image.isPrimary && remaining.length > 0) {
        const sorted = [...remaining].sort((a, b) => a.displayOrder - b.displayOrder);
        const promotedId = sorted[0].id;
        onChange(
          remaining.map((img) =>
            img.id === promotedId ? { ...img, isPrimary: true } : img,
          ),
        );
      } else {
        onChange(remaining);
      }
      // Keep replaceImage from being flagged as unused if the file shrinks.
      void replaceImage;
    } catch (err) {
      setError(err instanceof Error ? err.message : t('products.imageDeleteFailed'));
    } finally {
      setBusyImageId(null);
    }
  };

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold">{t('products.images')}</h3>
      <p className="text-sm text-muted-foreground">{t('products.imagesDescription')}</p>

      {sortedImages.length === 0 ? (
        <p className="text-sm text-muted-foreground italic">{t('products.noImagesYet')}</p>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
          {sortedImages.map((image) => {
            const src = getImageUrl(image.url) || undefined;
            const isBusy = busyImageId === image.id;
            return (
              <div
                key={image.id}
                className="relative border rounded-lg overflow-hidden bg-muted aspect-square group"
              >
                {src && (
                  <img
                    src={src}
                    alt=""
                    className="w-full h-full object-contain"
                  />
                )}
                {image.isPrimary && (
                  <span className="absolute top-2 left-2 inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold rounded-full bg-primary text-primary-foreground">
                    <Star className="w-3 h-3 fill-current" />
                    {t('products.primary')}
                  </span>
                )}
                <div className="absolute inset-x-0 bottom-0 flex items-center justify-between gap-2 p-2 bg-background/90 opacity-0 group-hover:opacity-100 transition-opacity">
                  {!image.isPrimary ? (
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      disabled={isBusy}
                      onClick={() => handleSetPrimary(image.id)}
                    >
                      <Star className="w-3 h-3 mr-1" />
                      {t('products.setAsPrimary')}
                    </Button>
                  ) : (
                    <span />
                  )}
                  <Button
                    type="button"
                    variant="destructive"
                    size="sm"
                    disabled={isBusy}
                    onClick={() => handleDelete(image)}
                    title={t('products.removeImage')}
                  >
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
                {isBusy && (
                  <div className="absolute inset-0 bg-background/70 flex items-center justify-center">
                    <Loader2 className="w-5 h-5 animate-spin" />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <div className="space-y-2">
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleUpload}
          className="hidden"
          id="product-image-upload"
        />
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
        >
          {uploading ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Upload className="w-4 h-4 mr-2" />
          )}
          {t('products.uploadImage')}
        </Button>
        {error && <p className="text-xs text-destructive">{error}</p>}
      </div>
    </div>
  );
}

interface PriceHistorySectionProps {
  orgId: string;
  productId: string;
  currency: string;
  // When the product's current price changes (e.g. admin just saved an edit),
  // it's used as a refetch trigger so the timeline reflects the new entry.
  currentPrice: string | null | undefined;
}

function PriceHistorySection({ orgId, productId, currency, currentPrice }: PriceHistorySectionProps) {
  const { t, i18n } = useTranslation(['admin']);
  const [entries, setEntries] = useState<ProductPriceHistoryEntry[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function fetchHistory() {
      setLoading(true);
      setError(null);
      const result = await listProductPriceHistory({
        path: { org_id: orgId, id: productId },
      });
      if (cancelled) return;
      if (result.error) {
        const err = result.error as ErrorResponse;
        setError(err.error?.message || t('products.priceHistoryFailedToLoad'));
        setLoading(false);
        return;
      }
      setEntries(result.data?.data ?? []);
      setLoading(false);
    }
    fetchHistory();
    return () => {
      cancelled = true;
    };
  }, [orgId, productId, currentPrice, t]);

  const dateFormatter = new Intl.DateTimeFormat(i18n.language, {
    dateStyle: 'medium',
    timeStyle: 'short',
  });

  const renderPrice = (price: string | null | undefined) => {
    const formatted = formatPrice(price, currency);
    return formatted ?? (
      <span className="italic text-muted-foreground">{t('products.priceHistoryNotSet')}</span>
    );
  };

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold">{t('products.priceHistory')}</h3>
      <p className="text-sm text-muted-foreground">{t('products.priceHistoryDescription')}</p>

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="w-4 h-4 animate-spin" />
        </div>
      ) : error ? (
        <p className="text-sm text-destructive">{error}</p>
      ) : !entries || entries.length === 0 ? (
        <p className="text-sm text-muted-foreground italic">{t('products.priceHistoryEmpty')}</p>
      ) : (
        <ol className="border-l-2 border-muted ml-2 space-y-4">
          {entries.map((entry) => {
            const isInitial = entry.oldPrice === null || entry.oldPrice === undefined;
            const actor = entry.changedByUsername
              ? t('products.priceHistoryBy', { name: entry.changedByUsername })
              : t('products.priceHistoryBySystem');
            return (
              <li key={entry.id} className="relative pl-4">
                <span className="absolute -left-[7px] top-1.5 w-3 h-3 rounded-full bg-primary" />
                <div className="text-sm font-medium">
                  {isInitial ? (
                    <span>
                      {t('products.priceHistoryInitial')}: {renderPrice(entry.newPrice)}
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-2 flex-wrap">
                      <span className="line-through text-muted-foreground">
                        {renderPrice(entry.oldPrice)}
                      </span>
                      <ArrowRight className="w-3 h-3 text-muted-foreground" />
                      <span>{renderPrice(entry.newPrice)}</span>
                    </span>
                  )}
                </div>
                <div className="text-xs text-muted-foreground mt-0.5">
                  {dateFormatter.format(new Date(entry.changedAt))} · {actor}
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}
